const getApiUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;

  if (envUrl) {
    return envUrl;
  }

  return 'http://localhost:5001';
};

const API_URL = getApiUrl();

class ApiService {
  constructor() {
    this.baseURL = API_URL;
  }

  getToken() {
    return localStorage.getItem('userToken');
  }

  getHeaders() {
    const token = this.getToken();

    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };
  }

  async get(endpoint) {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API GET error:', error);
      throw error;
    }
  }

  async post(endpoint, data) {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API POST error:', error);
      throw error;
    }
  }

  async put(endpoint, data) {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API PUT error:', error);
      throw error;
    }
  }

  async delete(endpoint) {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API DELETE error:', error);
      throw error;
    }
  }

  async login(email, password) {
    return this.post('/api/auth/login', { email, password });
  }

  async getProfile() {
    return this.get('/api/auth/profile');
  }

  async updateProfile(data) {
    return this.put('/api/auth/profile', data);
  }

  async changePassword(currentPassword, newPassword) {
    return this.put('/api/auth/change-password', { currentPassword, newPassword });
  }

  async uploadAvatar(file) {
    const formData = new FormData();
    formData.append('avatar', file);

    const response = await fetch(`${this.baseURL}/api/auth/avatar`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.getToken()}`
      },
      body: formData
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  async deleteAvatar() {
    return this.delete('/api/auth/avatar');
  }

  async getAnnouncements(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    return this.get(`/api/announcements${queryString ? `?${queryString}` : ''}`);
  }

  async getAnnouncement(id) {
    return this.get(`/api/announcements/${id}`);
  }

  async createAnnouncement(data) {
    return this.post('/api/announcements', data);
  }

  async updateAnnouncement(id, data) {
    return this.put(`/api/announcements/${id}`, data);
  }

  async deleteAnnouncement(id) {
    return this.delete(`/api/announcements/${id}`);
  }

  async toggleAnnouncementLike(id) {
    return this.post(`/api/announcements/${id}/like`);
  }

  async addAnnouncementComment(id, content) {
    return this.post(`/api/announcements/${id}/comment`, { content });
  }

  async getUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/api/users${queryString ? `?${queryString}` : ''}`);
  }

  async getDirectoryUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/api/users/directory${queryString ? `?${queryString}` : ''}`);
  }

  async getNewHires(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/api/users/new-hires${queryString ? `?${queryString}` : ''}`);
  }

  async getUserById(id) {
    return this.get(`/api/users/${id}`);
  }

  async updateUser(id, data) {
    return this.put(`/api/users/${id}`, data);
  }

  async deleteUser(id) {
    return this.delete(`/api/users/${id}`);
  }

  async getDepartments() {
    return this.get('/api/users/departments');
  }

  async getUserStats() {
    return this.get('/api/users/stats');
  }

  async getDocuments(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/api/documents${queryString ? `?${queryString}` : ''}`);
  }

  async getDocument(id) {
    return this.get(`/api/documents/${id}`);
  }

  async createDocument(data) {
    return this.post('/api/documents', data);
  }

  async updateDocument(id, data) {
    return this.put(`/api/documents/${id}`, data);
  }

  async deleteDocument(id) {
    return this.delete(`/api/documents/${id}`);
  }

  async uploadDocument(file, title = '', description = '') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('description', description);

    const response = await fetch(`${this.baseURL}/api/documents/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.getToken()}`
      },
      body: formData
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  async downloadDocument(id) {
    const response = await fetch(`${this.baseURL}/api/documents/${id}/download`, {
      method: 'GET',
      headers: {
        ...(this.getToken() && { Authorization: `Bearer ${this.getToken()}` })
      }
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch (parseError) {
        // Keep the default message when the response body is not JSON.
      }

      throw new Error(errorMessage);
    }

    return {
      blob: await response.blob(),
      filename: this.getFilenameFromDisposition(response.headers.get('content-disposition'))
    };
  }

  getFilenameFromDisposition(contentDisposition) {
    if (!contentDisposition) {
      return null;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);

    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const asciiMatch = contentDisposition.match(/filename="?(?<filename>[^"]+)"?/i);
    return asciiMatch?.groups?.filename || null;
  }

  async getAllUsers() {
    const response = await fetch(`${this.baseURL}/api/users`, {
      headers: {
        Authorization: `Bearer ${this.getToken()}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  async getCounts() {
    return this.get('/api/count');
  }

  async getManagedContent(section) {
    const queryString = section ? `?section=${encodeURIComponent(section)}` : '';
    return this.get(`/api/content${queryString}`);
  }

  async getHRStats() {
    return this.get('/api/hr/stats');
  }

  async getRecentApplications() {
    return this.get('/api/hr/recent-applications');
  }

  async getRecentNewEmployees() {
    return this.get('/api/hr/recent-employees');
  }

  async getLeaveRequests(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/api/hr/leave-requests${queryString ? `?${queryString}` : ''}`);
  }

  async getExpenseReports(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/api/hr/expense-reports${queryString ? `?${queryString}` : ''}`);
  }

  async getCareerOpportunities(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/api/hr/career-opportunities${queryString ? `?${queryString}` : ''}`);
  }

  async approveApplication(id, type, approverId) {
    return this.put(`/api/hr/applications/${id}/${type}/approve`, { approverId });
  }

  async rejectApplication(id, type, approverId, reason) {
    return this.put(`/api/hr/applications/${id}/${type}/reject`, { approverId, reason });
  }

  async createEmployee(employeeData) {
    const response = await fetch(`${this.baseURL}/api/users/employee`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.getToken()}`
      },
      body: JSON.stringify(employeeData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Çalışan oluşturulurken hata oluştu');
    }

    return await response.json();
  }

  async getInventory(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/api/inventory${queryString ? `?${queryString}` : ''}`);
  }

  async getInventoryStats() {
    return this.get('/api/inventory/stats');
  }

  async createInventoryItem(itemData) {
    return this.post('/api/inventory', itemData);
  }

  async updateInventoryItem(id, itemData) {
    return this.put(`/api/inventory/${id}`, itemData);
  }

  async deleteInventoryItem(id) {
    return this.delete(`/api/inventory/${id}`);
  }

  async getInventoryItemById(id) {
    return this.get(`/api/inventory/${id}`);
  }

  async getITSupportTickets() {
    return this.get('/api/it-support/tickets');
  }

  async createITSupportTicket(data) {
    return this.post('/api/it-support/tickets', data);
  }

  async getITSupportStats() {
    return this.get('/api/it-support/stats');
  }

  async getSurveys() {
    return this.get('/api/surveys');
  }

  async createSurvey(data) {
    return this.post('/api/surveys', data);
  }

  async submitSurveyResponse(id, data) {
    return this.post(`/api/surveys/${id}/respond`, data);
  }

  async getSurveyResults(id) {
    return this.get(`/api/surveys/${id}/results`);
  }

  async getEvents() {
    return this.get('/api/events');
  }

  async createEvent(data) {
    return this.post('/api/events', data);
  }

  async respondToEvent(id, data) {
    return this.post(`/api/events/${id}/respond`, data);
  }

  async getEventHighlights() {
    return this.get('/api/events/highlights');
  }

  async getInnovations() {
    return this.get('/api/innovations');
  }

  async createInnovation(data) {
    return this.post('/api/innovations', data);
  }

  async toggleInnovationVote(id) {
    return this.post(`/api/innovations/${id}/vote`);
  }
}

const apiService = new ApiService();
export default apiService;
